import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Send, Loader2, FolderPlus, MessageSquare, FileUp, Play,
  CheckCircle2, X, File, Maximize2, HardHat, Square
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  { label: 'Create new project', icon: FolderPlus },
  { label: 'What projects do I have?', icon: MessageSquare },
  { label: 'Help me estimate a takeoff', icon: MessageSquare },
];

const WIZARD_STEPS = {
  IDLE: 'idle',
  ASKED_NAME: 'asked_name',
  UPLOADING_DRAWING: 'uploading_drawing',
  ASKING_SPEC: 'asking_spec',
  UPLOADING_SPEC: 'uploading_spec',
  CONFIRMING_ANALYSIS: 'confirming_analysis',
  ANALYSIS_STARTED: 'analysis_started',
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [wizardState, setWizardState] = useState(WIZARD_STEPS.IDLE);
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [currentProjectName, setCurrentProjectName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);

  const stopGenerating = () => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setLoading(false);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const hasMessages = messages.length > 0;

  const addMessage = useCallback((role, content, meta = {}) => {
    setMessages(prev => [...prev, { role, content, meta, ts: Date.now() }]);
  }, []);

  const ensureSession = async () => {
    if (sessionId) return sessionId;
    try {
      const res = await fetch(`${API}/chat/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ title: 'New Chat' })
      });
      if (res.ok) {
        const session = await res.json();
        setSessionId(session.session_id);
        return session.session_id;
      }
    } catch (e) { console.error('Create session error:', e); }
    return null;
  };

  const sendMessage = async (text) => {
    if (!text.trim()) return;
    const userMsg = text.trim();
    addMessage('user', userMsg);
    setInput('');
    setLoading(true);

    if (wizardState === WIZARD_STEPS.ASKED_NAME) {
      await handleProjectNameProvided(userMsg);
      setLoading(false);
      return;
    }

    if (wizardState === WIZARD_STEPS.ASKING_SPEC) {
      const isYes = /^(yes|y|yeah|yep|sure|ok|yea|upload)/i.test(userMsg.trim());
      if (isYes) {
        addMessage('assistant', 'Please upload your spec sheet.', { showUpload: true, uploadType: 'spec' });
        setWizardState(WIZARD_STEPS.UPLOADING_SPEC);
      } else {
        addMessage('assistant', 'No worries! Ready to start the AI analysis on your drawings?', { showConfirm: true, confirmAction: 'analyze' });
        setWizardState(WIZARD_STEPS.CONFIRMING_ANALYSIS);
      }
      setLoading(false);
      return;
    }

    if (wizardState === WIZARD_STEPS.CONFIRMING_ANALYSIS) {
      const isYes = /^(yes|y|yeah|yep|sure|ok|yea|start|go)/i.test(userMsg.trim());
      if (isYes) {
        await handleStartAnalysis();
      } else {
        addMessage('assistant', 'No problem. Your project is ready whenever you are.');
        setWizardState(WIZARD_STEPS.IDLE);
        setCurrentProjectId(null);
      }
      setLoading(false);
      return;
    }

    const sid = await ensureSession();
    if (!sid) {
      addMessage('assistant', "Sorry, couldn't start a session. Please try again.");
      setLoading(false);
      return;
    }

    try {
      abortRef.current = new AbortController();
      const res = await fetch(`${API}/chat/sessions/${sid}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: userMsg }),
        signal: abortRef.current.signal,
      });
      if (!res.ok) throw new Error('Chat failed');
      const data = await res.json();

      if (data.action?.action === 'create_project') {
        const suggestedName = data.action.project_name;
        addMessage('assistant', data.response || (suggestedName ? `I'll create "${suggestedName}". Sound good, or want a different name?` : "What would you like to name your project?"));
        setWizardState(WIZARD_STEPS.ASKED_NAME);
      } else if (data.action?.action === 'start_analysis' && data.action.project_id) {
        setCurrentProjectId(data.action.project_id);
        await handleStartAnalysis(data.action.project_id);
        addMessage('assistant', data.response || "Starting analysis...");
      } else {
        addMessage('assistant', data.response || "I'm not sure how to help with that.");
      }
    } catch (e) {
      if (e.name === 'AbortError') {
        addMessage('assistant', "(stopped by user)");
      } else {
        addMessage('assistant', "Sorry, I'm having trouble right now. Please try again.");
      }
    } finally {
      setLoading(false);
      abortRef.current = null;
    }
  };

  const handleProjectNameProvided = async (name) => {
    try {
      const res = await fetch(`${API}/home-chat/create-project`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name })
      });
      if (!res.ok) throw new Error('Failed');
      const project = await res.json();
      setCurrentProjectId(project.project_id);
      setCurrentProjectName(name);
      addMessage('assistant', `Project "${name}" created! Upload your construction drawing (PDF).`, { showUpload: true, uploadType: 'drawing' });
      setWizardState(WIZARD_STEPS.UPLOADING_DRAWING);
    } catch (e) {
      addMessage('assistant', "Couldn't create the project. Please try again.");
      setWizardState(WIZARD_STEPS.IDLE);
    }
  };

  const handleFileUpload = async (file, type) => {
    if (!file || !currentProjectId) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', type);
    try {
      const res = await fetch(`${API}/home-chat/upload/${currentProjectId}`, {
        method: 'POST', credentials: 'include', body: formData
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      const fileName = data.file?.filename || file.name;
      const pages = data.file?.pages || 0;
      addMessage('user', `Uploaded: ${fileName}`, { isFile: true });
      if (type === 'drawing') {
        addMessage('assistant', `Drawing uploaded (${pages} pages). Do you have a spec sheet to upload?`, { showSpecOptions: true });
        setWizardState(WIZARD_STEPS.ASKING_SPEC);
      } else {
        addMessage('assistant', `Spec sheet uploaded (${pages} pages). Ready to run the AI takeoff analysis?`, { showAnalyzeBtn: true });
        setWizardState(WIZARD_STEPS.CONFIRMING_ANALYSIS);
      }
    } catch (e) {
      addMessage('assistant', 'Upload failed. Please try again.');
    } finally { setUploading(false); }
  };

  const handleStartAnalysis = async (projectId) => {
    const pid = projectId || currentProjectId;
    if (!pid) return;
    try {
      const res = await fetch(`${API}/home-chat/analyze/${pid}`, { method: 'POST', credentials: 'include' });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Failed'); }
      addMessage('assistant', 'Analysis started! Track progress on the project page.', { showGoToProject: true, projectId: pid });
      setWizardState(WIZARD_STEPS.ANALYSIS_STARTED);
    } catch (e) {
      addMessage('assistant', `Couldn't start analysis: ${e.message}`);
      setWizardState(WIZARD_STEPS.IDLE);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); }
  };

  const FileDropZone = ({ uploadType }) => {
    const [dragOver, setDragOver] = useState(false);
    const localRef = useRef(null);
    return (
      <div
        data-testid={`upload-${uploadType}-dropzone`}
        className={`mt-3 border-2 border-dashed rounded-xl p-5 flex flex-col items-center justify-center text-center cursor-pointer transition-all
          ${dragOver ? 'border-[#0D1830] bg-[#0D1830]/3' : 'border-[#72A4BF]/40 bg-[#F7FAFB] hover:border-[#446E87]'}
        `}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) handleFileUpload(f, uploadType); }}
        onClick={() => localRef.current?.click()}
      >
        <input ref={localRef} type="file" accept=".pdf" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileUpload(f, uploadType); }} />
        {uploading ? (
          <div className="flex items-center gap-2"><Loader2 className="w-5 h-5 text-[#0D1830] animate-spin" /><span className="text-sm font-medium text-[#0D1830]">Uploading...</span></div>
        ) : (
          <><FileUp className="w-7 h-7 text-[#72A4BF] mb-2" /><p className="text-sm font-semibold text-[#0D1830]">{uploadType === 'drawing' ? 'Upload Construction Drawing' : 'Upload Spec Sheet'}</p><p className="text-xs text-[#72A4BF] mt-1">Drag & drop or click (PDF)</p></>
        )}
      </div>
    );
  };

  const ConfirmButtons = ({ action }) => (
    <div className="flex items-center gap-2 mt-3" data-testid={`confirm-${action}`}>
      <Button data-testid={`confirm-${action}-yes`} onClick={() => {
        if (action === 'spec') { addMessage('user', 'Yes'); addMessage('assistant', 'Upload your spec sheet.', { showUpload: true, uploadType: 'spec' }); setWizardState(WIZARD_STEPS.UPLOADING_SPEC); }
        else if (action === 'analyze') { addMessage('user', 'Yes, start analysis'); handleStartAnalysis(); }
      }} className="bg-[#0D1830] text-white font-bold px-4 py-2 rounded-lg hover:bg-[#1D4053] text-sm">
        <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />Yes
      </Button>
      <Button data-testid={`confirm-${action}-no`} variant="outline" onClick={() => {
        addMessage('user', 'No');
        if (action === 'spec') { addMessage('assistant', 'Ready to start the AI analysis?', { showConfirm: true, confirmAction: 'analyze' }); setWizardState(WIZARD_STEPS.CONFIRMING_ANALYSIS); }
        else { addMessage('assistant', `Project "${currentProjectName}" is saved. Start analysis anytime.`); setWizardState(WIZARD_STEPS.IDLE); setCurrentProjectId(null); }
      }} className="border-[#0D1830]/20 text-[#0D1830] font-bold px-4 py-2 rounded-lg text-sm">
        <X className="w-3.5 h-3.5 mr-1.5" />No
      </Button>
    </div>
  );

  const SpecOptions = () => (
    <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="spec-options">
      <Button data-testid="upload-spec-btn" onClick={() => {
        addMessage('user', 'Yes, I have a spec sheet');
        addMessage('assistant', 'Upload your spec sheet.', { showUpload: true, uploadType: 'spec' });
        setWizardState(WIZARD_STEPS.UPLOADING_SPEC);
      }} className="bg-[#0D1830] text-white font-bold px-4 py-2 rounded-lg hover:bg-[#1D4053] text-sm">
        <FileUp className="w-3.5 h-3.5 mr-1.5" />Upload Spec Sheet
      </Button>
      <Button data-testid="no-spec-btn" variant="outline" onClick={() => {
        addMessage('user', "I don't have a spec sheet");
        addMessage('assistant', 'No problem! Ready to run the AI takeoff analysis?', { showAnalyzeBtn: true });
        setWizardState(WIZARD_STEPS.CONFIRMING_ANALYSIS);
      }} className="border-[#0D1830]/20 text-[#0D1830] font-bold px-4 py-2 rounded-lg text-sm">
        I don't have a spec sheet
      </Button>
      <Button data-testid="go-to-project-early-btn" variant="outline" onClick={() => {
        navigate(`/projects/${currentProjectId}`);
      }} className="border-[#0D1830]/20 text-[#446E87] font-medium px-4 py-2 rounded-lg text-sm">
        Go to Project Page
      </Button>
    </div>
  );

  const AnalyzeButton = () => (
    <div className="flex items-center gap-2 mt-3" data-testid="analyze-options">
      <Button data-testid="start-analysis-btn" onClick={() => {
        addMessage('user', 'Start the analysis');
        handleStartAnalysis();
      }} className="bg-[#0D1830] text-white font-bold px-4 py-2 rounded-lg hover:bg-[#1D4053] text-sm">
        <Play className="w-3.5 h-3.5 mr-1.5" />Analyze
      </Button>
      <Button data-testid="go-to-project-skip-btn" variant="outline" onClick={() => {
        navigate(`/projects/${currentProjectId}`);
      }} className="border-[#0D1830]/20 text-[#446E87] font-medium px-4 py-2 rounded-lg text-sm">
        Go to Project Page
      </Button>
    </div>
  );

  return (
    <div className="flex flex-col h-full bg-[#F7FAFB]" data-testid="dashboard-page">
      {/* Messages or Empty State */}
      <div className={`flex-1 overflow-y-auto ${hasMessages ? 'px-6 py-6' : 'flex items-center justify-center'}`} data-testid="chat-messages">
        {!hasMessages ? (
          <div className="w-full max-w-2xl mx-auto px-6 text-center" data-testid="chat-empty-state">
            <div className="w-16 h-16 rounded-2xl bg-[#0D1830]/5 flex items-center justify-center mx-auto mb-6">
              <HardHat className="w-8 h-8 text-[#0D1830]" />
            </div>
            <h1 className="text-2xl font-extrabold text-[#0D1830] tracking-tight mb-2">What can I help you build?</h1>
            <p className="text-sm text-[#72A4BF] mb-8">Create projects, run takeoffs, or ask anything about your estimates</p>
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  data-testid={`suggestion-${i}`}
                  onClick={() => sendMessage(s.label)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#0D1830]/10 text-sm font-medium text-[#0D1830] hover:bg-white hover:border-[#0D1830]/20 hover:shadow-sm transition-all"
                >
                  <s.icon className="w-4 h-4 text-[#446E87]" />
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="max-w-3xl mx-auto space-y-4">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`chat-msg-${i}`}>
                <div className={`max-w-[80%] ${
                  msg.role === 'user'
                    ? 'bg-[#0D1830] text-white px-5 py-3 rounded-2xl rounded-tr-md'
                    : 'bg-white border border-[#0D1830]/8 text-[#0D1830] px-5 py-3 rounded-2xl rounded-tl-md shadow-sm'
                }`}>
                  {msg.meta?.isFile ? (
                    <div className="flex items-center gap-2"><File className="w-4 h-4 shrink-0" /><span className="text-sm font-medium">{msg.content}</span></div>
                  ) : (
                    <p className="text-sm font-medium leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                  )}
                  {msg.meta?.showUpload && <FileDropZone uploadType={msg.meta.uploadType} />}
                  {msg.meta?.showConfirm && <ConfirmButtons action={msg.meta.confirmAction} />}
                  {msg.meta?.showSpecOptions && <SpecOptions />}
                  {msg.meta?.showAnalyzeBtn && <AnalyzeButton />}
                  {msg.meta?.showGoToProject && (
                    <Button data-testid="go-to-project-btn" onClick={() => navigate(`/projects/${msg.meta.projectId}?tab=takeoff`)} className="mt-3 bg-[#0D1830] text-white font-bold px-4 py-2 rounded-lg hover:bg-[#1D4053] text-sm">
                      <Play className="w-3.5 h-3.5 mr-1.5" />Go to Project
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-[#0D1830]/8 px-5 py-3 rounded-2xl rounded-tl-md shadow-sm">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-[#0D1830] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-[#0D1830] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-[#0D1830] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input Area — always at bottom */}
      <div className="bg-white border-t border-[#0D1830]/10 px-6 py-4 shrink-0">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <Input
            ref={inputRef}
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              wizardState === WIZARD_STEPS.ASKED_NAME ? "Enter project name..." :
              "Message Nivela AI..."
            }
            className="flex-1 bg-[#F7FAFB] border-[#0D1830]/10 rounded-xl px-4 py-3 text-sm font-medium text-[#0D1830] focus-visible:ring-[#0D1830]/20"
            disabled={loading}
          />
          {loading ? (
            <Button
              data-testid="chat-stop-btn"
              onClick={stopGenerating}
              className="bg-[#0D1830] text-white rounded-xl px-4 py-3 hover:bg-[#1D4053]"
              title="Stop generating"
            >
              <Square className="w-4 h-4" />
            </Button>
          ) : (
            <Button
              data-testid="chat-send-btn"
              onClick={() => sendMessage(input)}
              disabled={!input.trim()}
              className="bg-[#0D1830] text-white rounded-xl px-4 py-3 hover:bg-[#1D4053] disabled:opacity-40"
            >
              <Send className="w-4 h-4" />
            </Button>
          )}
        </div>
        <p className="text-center text-[10px] text-[#72A4BF] mt-2 max-w-3xl mx-auto">
          Nivela AI can make mistakes. Verify important takeoff quantities.
        </p>
      </div>
    </div>
  );
}
